/** @jsxImportSource @emotion/react */
import { PageWrapper } from "../../components/page-skeleton/PageWrapper";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { SpinnerBlock } from "../common/Spinner";
import { MenuTable } from "./components/MenuTable";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { css } from "@emotion/react";
import { MutateMenusOnSalesChannelsSelector } from "./components/MutateMenusOnSalesChannelsSelector";
import {
  ButtonExternalLink,
  DropdownButton,
} from "../../components/NewButtons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { useGetMenus } from "./service/menus-api";
import { MenuProps } from "antd";
import { EmptyState } from "../../components/EmptyState";
import { Divider } from "../../components/Divider";
import { MutateMenuDrawer } from "./components/MutateMenuDrawer";
import { useState } from "react";
import { DropdownItemButton } from "../common/DropdownItemButton";
import { LoadingModal } from "../../components/LoadingModal";
import { CreateMenuFromShopModal } from "./components/CreateMenuFromShopModal";
import { useHandleFetchError } from "../../error-handler";

const menuHelpCenterLink =
  "https://heapsapp.notion.site/Menus-c64d985ceed44706b8e8a28a6fd88568?pvs=4";

const cardBodyAdditionalStyling = css`
  padding-bottom: 24px;
`;

export const MenuList = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [createMenuModalOpen, setCreateMenuModalOpen] = useState<boolean>(
    false
  );
  const [
    createMenuFromShopModalOpen,
    setCreateMenuFromShopModalOpen,
  ] = useState<boolean>(false);
  const [
    menuGenerationIsLoading,
    setMenuGenerationIsLoading,
  ] = useState<boolean>(false);

  const {
    data: menus,
    isLoading: menusAreLoading,
    error: getMenusError,
  } = useGetMenus(organizationKeyName);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <DropdownItemButton
          onClick={() => setCreateMenuModalOpen(true)}
          type="button"
          icon={<FontAwesomeIcon icon={solid("plus")} />}
        >
          Create new menu
        </DropdownItemButton>
      ),
    },
    {
      key: "2",
      label: (
        <DropdownItemButton
          onClick={() => setCreateMenuFromShopModalOpen(true)}
          type="button"
          icon={<FontAwesomeIcon icon={solid("copy")} />}
        >
          Create menu with existing restaurant products
        </DropdownItemButton>
      ),
    },
  ];

  useHandleFetchError(getMenusError);

  if (menusAreLoading) {
    return <SpinnerBlock />;
  }

  return (
    <>
      <PageWrapper>
        <CardContainer>
          <PageHeader
            title="Menus"
            subtitle="Create, edit and manage menus for restaurants"
          >
            <ButtonExternalLink
              href={menuHelpCenterLink}
              icon={
                <FontAwesomeIcon icon={solid("arrow-up-right-from-square")} />
              }
            >
              Learn more
            </ButtonExternalLink>
            <MutateMenusOnSalesChannelsSelector />
            <DropdownButton
              menu={{
                items,
              }}
              buttonSize="large"
              rightIcon={<FontAwesomeIcon icon={solid("chevron-down")} />}
            >
              Create menu
            </DropdownButton>
          </PageHeader>
          {menus?.length === 0 || !menus ? (
            <>
              <Divider />
              <CardBody additionalStyles={cardBodyAdditionalStyling}>
                <EmptyState
                  title="No menus created yet"
                  subtitle="Click in the top right corner to create your first new menu or generate a menu from existing restaurant products."
                />
              </CardBody>
            </>
          ) : (
            <MenuTable menus={menus} />
          )}
        </CardContainer>
        <MutateMenuDrawer
          isOpen={createMenuModalOpen}
          onClose={() => setCreateMenuModalOpen(false)}
        />
        <CreateMenuFromShopModal
          isOpen={createMenuFromShopModalOpen}
          onClose={() => setCreateMenuFromShopModalOpen(false)}
          setMenuGenerationIsLoading={setMenuGenerationIsLoading}
        />
        <LoadingModal
          isOpen={menuGenerationIsLoading}
          header="Creating menu..."
        />
      </PageWrapper>
    </>
  );
};
